import ColorHash from "color-hash";
import isEqual from "lodash/isEqual";

import {
  Action,
  ActionType,
  LayoutOrientation,
  isAddClipToImagePanelAction,
  isAddPathToMapPanelAction,
  isAddSeriesToPlotPanelAction,
  isCreatePanelAction,
  isMovePanelAction,
  isPutImagePanelDataAction,
  isPutFilesAction,
  isPutRawMessagePanelDataAction,
  isRemoveAllPanelsAction,
  isRemovePanelAction,
  isRemovePathFromMapPanelAction,
  isRemoveSeriesFromPlotPanelAction,
  isReplaceStateAction,
  isResizeLayoutsAction,
  isSetAllLayoutsResizingAction,
  isSetDataSourceAction,
  isSetDatasourcesAction,
  isSetEventVisibilityAction,
  isSetVisibilityForAllEventsAction,
  isSetImagePanelConfigAction,
  isSetLayoutResizingAction,
  isSetLogPanelConfigAction,
  isSetPathCoordinateUnitsAction,
  isSetPathStyleAction,
  isSetPathVisibilityAction,
  isSetSeriesConfigAction,
  isSetSeriesVisibilityAction,
  isAddDataToLogPanelAction,
  isRemoveMessagePathFromLogPanelAction,
  isRemoveClipFromImagePanelAction,
  isReplacePanelsAndLayoutAction,
  isSetPlotPanelConfigAction,
  isSetPanelTitleAction,
  isSetThreeDPanelConfigAction,
  isAddThreeDEntityAction,
  isUpdateThreeDEntityAction,
  isRemoveThreeDEntityAction,
  isSetDocumentPanelFileAction,
  isCreateDocumentPanelWithFileAction,
} from "../actions";
import { assignSeriesColor } from "../assignSeriesColor";
import {
  INITIAL_STATE,
  DEFAULT_IMAGE_PANEL_CONFIG,
  DEFAULT_PLOT_PANEL_CONFIG,
} from "../context";
import { cyclingColorScale } from "../CyclingColorScale";
import { selectLayoutById } from "../hooks";
import { idForClip, idForMap, idForSeries } from "../identifiers";
import {
  type DocumentPanelState,
  type LayoutItem,
  isDocumentPanelState,
  isImagePanelState,
  isLayoutItem,
  isLogPanelState,
  isMapPanelState,
  isPlotPanelState,
  isRawMessagePanelState,
  isThreeDPanelState,
  LayoutType,
  PanelType,
  PlotSeries,
  State,
  TopicData,
} from "../schema";
import {
  ImagePanelClip,
  MapPath,
  PlotAxisConfig,
  PlotPanelConfig,
  PlotPanelState,
  ThreeDPanelState,
  PlotSeriesConfig,
  PlotYAxis,
  ImagePanelState,
  RawMessagePanelState,
} from "../schema/v1";
import { DEFAULT_ENTITY_ID } from "../schema/v1/three-d";

import { addItemToLayout } from "./addItemToLayout";
import { removeItemFromLayout } from "./removeItemFromLayout";
import { setIsLayoutResizing } from "./setIsLayoutResizing";
import { updateLayout } from "./updateLayout";

const colorHash = new ColorHash({ lightness: 0.5, saturation: 0.5 });

function titleForClips(data: ImagePanelClip[]): string {
  if (data.length === 0) {
    return "";
  }

  const firstTopicName = data[0].data.topic.name;

  let topicPart = "Multiple topics";

  // If every clip has the same topic name
  if (data.every((clip) => clip.data.topic.name === firstTopicName)) {
    topicPart = firstTopicName;
  }

  if (data.length === 1) {
    return topicPart;
  } else {
    return `${topicPart} (${data.length} clips)`;
  }
}

function titleForPlot(data: PlotSeries[]): string {
  if (data.length === 0) {
    return "";
  }

  if (data.length === 1) {
    const series = data[0];
    return `${series.data.topic.name}.${series.data.messagePath.dotPath}`;
  }

  return "Multiple topics";
}

function titleForMap(data: MapPath[]): string {
  if (data.length === 0) {
    return "";
  }

  // If there's 1 path
  if (data.length === 1 && data[0].data.length > 0) {
    const path = data[0];
    return `${path.data[0].topic.name}`;
  }

  // If there are multiple paths
  return "Multiple paths";
}

function titleForLogPanel(data: TopicData[]): string {
  if (data.length === 0) {
    return "";
  }
  if (data.length === 1) {
    return `${data[0].topic.name}.${data[0].messagePath.dotPath}`;
  }
  return data[0].topic.name;
}

const defaultMapPathColor = "#007cbf";

export function reducer<T>(state: State, action: Action<T>): State {
  switch (action.type) {
    case ActionType.AddClipToImagePanel: {
      if (!isAddClipToImagePanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isImagePanelState(panel)) {
        return state;
      }

      const id = idForClip();

      const clip: ImagePanelClip = {
        data: action.payload.data,
        id,
      };

      const nextData = [...panel.data, clip];
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForClips(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.AddDataToLogPanel: {
      if (!isAddDataToLogPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isLogPanelState(panel)) {
        return state;
      }

      const nextData = [...panel.data, action.payload.data];
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForLogPanel(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.AddPathToMapPanel: {
      if (!isAddPathToMapPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isMapPanelState(panel)) {
        return state;
      }

      const id = idForMap(action.payload.data);

      if (panel.data.some((path) => path.id === id)) {
        return state;
      }

      let color = defaultMapPathColor;
      if (panel.data.length > 0) {
        color = colorHash.hex(id);
      }

      const path = {
        coordinateUnits: action.payload.coordinateUnits,
        data: action.payload.data,
        id,
        visible: true,
        style: {
          lineColor: color,
        },
      };

      const nextData = [...panel.data, path];
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForMap(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.AddSeriesToPlotPanel: {
      if (!isAddSeriesToPlotPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isPlotPanelState(panel)) {
        return state;
      }

      const id = idForSeries();

      const series: PlotSeries = {
        data: action.payload.data,
        id,
        config: {
          lineColor: assignSeriesColor(panel.id, action.payload.data),
        },
        visible: true,
      };

      const nextData = [...panel.data, series];
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForPlot(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.CreatePanel: {
      if (!isCreatePanelAction(action)) {
        return state;
      }

      const base = { id: crypto.randomUUID() };

      const updatedPanels = {
        ...state.panels,
      };

      let payloadData = null;

      if (action.payload.data && action.payload.data.length === 1) {
        payloadData = action.payload.data[0];
      }

      if (action.payload.type === PanelType.Plot) {
        if (payloadData === null) {
          const panel = {
            ...base,
            type: action.payload.type,
            config: DEFAULT_PLOT_PANEL_CONFIG,
            data: [],
            title: "",
          };
          updatedPanels[panel.id] = panel;
        } else {
          const series: PlotSeries = {
            config: {
              lineColor: assignSeriesColor(base.id, payloadData),
            },
            data: payloadData,
            id: idForSeries(),
            visible: true,
          };
          const panel = {
            ...base,
            type: action.payload.type,
            config: DEFAULT_PLOT_PANEL_CONFIG,
            data: [series],
            title: titleForPlot([series]),
          };
          updatedPanels[panel.id] = panel;
        }
      } else if (action.payload.type === PanelType.ThreeD) {
        const panel: ThreeDPanelState = {
          ...base,
          title: "",
          type: action.payload.type,
          config: {
            camera: {
              type: "orbit",
              target: { id: "entity0", mesh: "mesh" },
              zoom: {},
            },
            grid: {
              color: "#88888888",
              scale: 5,
            },
          },
          data: {
            entities: {
              entity0: {
                id: DEFAULT_ENTITY_ID,
                mesh: {
                  type: "sphere",
                  color: "#FF0000",
                  // color: "#DF6888",
                  // color: "#447799",
                  radius: 1,
                },
                path: {
                  color: "#447799",
                  lineWidth: 4,
                },
                position: {
                  forward: 0,
                  forwardScale: 1,
                  up: 0,
                  upScale: 1,
                  right: 0,
                  rightScale: 1,
                },
              },
            },
          },
        };
        updatedPanels[panel.id] = panel;
      } else if (action.payload.type === PanelType.Image) {
        const clips =
          payloadData !== null
            ? [
                {
                  data: payloadData,
                  id: idForClip(),
                },
              ]
            : [];

        const panel: ImagePanelState = {
          ...base,
          type: PanelType.Image,
          config: DEFAULT_IMAGE_PANEL_CONFIG,
          data: clips,
          title: titleForClips(clips),
        };
        updatedPanels[panel.id] = panel;
      } else if (action.payload.type === PanelType.Log) {
        if (payloadData === null) {
          const panel = {
            ...base,
            type: action.payload.type,
            data: [],
            title: "",
          };
          updatedPanels[panel.id] = panel;
        } else {
          const panel = {
            ...base,
            type: action.payload.type,
            data: [payloadData],
            title: titleForLogPanel([payloadData]),
          };
          updatedPanels[panel.id] = panel;
        }
      } else if (action.payload.type === PanelType.Document) {
        const panel = {
          ...base,
          type: action.payload.type,
          data: null,
          title: "",
        };
        updatedPanels[panel.id] = panel;
      } else if (action.payload.type === PanelType.Map) {
        if (action.payload.data === null) {
          const panel = {
            ...base,
            type: action.payload.type,
            data: [],
            title: "",
          };
          updatedPanels[panel.id] = panel;
        } else {
          const path = {
            data: action.payload.data,
            id: idForMap(action.payload.data),
            visible: true,
            style: {
              lineColor: defaultMapPathColor,
            },
          };
          const panel = {
            ...base,
            type: action.payload.type,
            data: [path],
            title: titleForMap([path]),
          };
          updatedPanels[panel.id] = panel;
        }
      } else {
        const panel: RawMessagePanelState = {
          ...base,
          type: action.payload.type,
          data: payloadData,
          title: payloadData?.topic.name ?? "",
        };
        updatedPanels[panel.id] = panel;
      }

      const nextState: State = {
        ...state,
        panels: updatedPanels,
      };

      // If placement not provided we add the new panel to the left of the root layout
      const placement = action.payload.placement ?? {
        siblingLayout: state.layout,
        orientation: LayoutOrientation.LEFT,
      };

      const layoutItem: LayoutItem = {
        id: base.id,
        isResizing: false,
        relativeSize: 1,
        type: LayoutType.Panel,
      };

      return addItemToLayout(nextState, layoutItem, placement);
    }
    case ActionType.MovePanel: {
      if (!isMovePanelAction(action)) {
        return state;
      }

      const nextState = removeItemFromLayout(state, action.payload.panelLayout);

      // Special case:
      // We currently have the ability to specify a layout as the drop target.
      // As a result, there is a possibility that when we move a panel out of a layout that contains
      // just two children, one of which is the panel we're moving, we delete that layout and merge its
      // remaining child up the tree.
      // So to handle that we set the siblingLayout to be that layout's child if the layout no longer exists
      let { siblingLayout } = action.payload.placement;

      const layout = selectLayoutById(nextState, siblingLayout.id);

      if (layout === undefined) {
        if (isLayoutItem(siblingLayout) === false) {
          if (siblingLayout.children.length === 2) {
            const newSiblingLayout = siblingLayout.children.find(
              (child) => child.id !== action.payload.panelLayout.id,
            );
            if (newSiblingLayout !== undefined) {
              siblingLayout = newSiblingLayout;
            }
          }
        }
      } else {
        // Update siblingLayout with the new size from removing the old panel
        siblingLayout = layout;
      }

      return addItemToLayout(nextState, action.payload.panelLayout, {
        ...action.payload.placement,
        siblingLayout,
      });
    }
    case ActionType.PutImagePanelData: {
      if (!isPutImagePanelDataAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isImagePanelState(panel)) {
        return state;
      }

      const newClips = [...(panel.data ?? [])];

      // Only add the clip if it's not already in the panel
      if (
        !newClips.some(
          (clip) =>
            clip.data.messagePath.id === action.payload.data.messagePath.id,
        )
      ) {
        newClips.push({
          data: action.payload.data,
          id: idForClip(),
        });
      }

      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          // Reset config to defaults if data changes.
          // If previous image was captured upside down or needed to be stretched to fit the canvas,
          // this one may not be.
          config: DEFAULT_IMAGE_PANEL_CONFIG,
          data: newClips,
          title: titleForClips(newClips),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.PutFiles: {
      if (!isPutFilesAction(action)) {
        return state;
      }

      return {
        ...state,
        files: action.payload.files,
        datasources: action.payload.files.map((file) => {
          return {
            type: "file" as const,
            value: file,
          };
        }),
      };
    }
    case ActionType.SetDatasources: {
      if (!isSetDatasourcesAction(action)) {
        return state;
      }

      // Extract files from datasources for backwards compatibility
      const files = action.payload.datasources
        .filter(
          (ds): ds is { type: "file"; value: { fileId: string } } =>
            ds.type === "file",
        )
        .map((ds) => ds.value);

      return {
        ...state,
        datasources: action.payload.datasources,
        files,
      };
    }
    case ActionType.PutRawMessagePanelData: {
      if (!isPutRawMessagePanelDataAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isRawMessagePanelState(panel)) {
        return state;
      }

      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: action.payload.data,
          title: action.payload.data.topic.name,
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.RemoveAllPanels: {
      if (!isRemoveAllPanelsAction(action)) {
        return state;
      }

      // Clean up state potentially left behind in stateful color scale
      cyclingColorScale.reset();

      return {
        ...state,
        panels: { ...INITIAL_STATE.panels },
        layout: {
          ...INITIAL_STATE.layout,
          children: [],
        },
      };
    }

    case ActionType.RemoveClipFromImagePanel: {
      if (!isRemoveClipFromImagePanelAction(action)) {
        return state;
      }

      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isImagePanelState(panel) || !panel.data) {
        return state;
      }

      const nextData =
        panel.data.filter((data) => data.id !== action.payload.clipId) ?? [];

      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForClips(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }

    case ActionType.RemoveMessagePathFromLogPanel: {
      if (!isRemoveMessagePathFromLogPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isLogPanelState(panel)) {
        return state;
      }

      const nextData = panel.data.filter(
        (data) => data.messagePath.id !== action.payload.messagePathId,
      );

      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForLogPanel(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.RemovePanel: {
      if (!isRemovePanelAction(action)) {
        return state;
      }

      // Remove panel
      const existing = state.panels ?? {};
      const updatedPanels = { ...existing };
      delete updatedPanels[action.payload.panelId];
      // Clean up state potentially left behind in stateful color scale
      cyclingColorScale.resetForPanel(action.payload.panelId);

      const nextState: State = {
        ...state,
        panels: updatedPanels,
      };

      // Find layout for panel id
      const panelLayout = selectLayoutById(nextState, action.payload.panelId);

      if (panelLayout === undefined || !isLayoutItem(panelLayout)) {
        // Should never happen. A corresponding layout should exist for a panel in state
        return nextState;
      }

      return removeItemFromLayout(nextState, panelLayout);
    }
    case ActionType.RemovePathFromMapPanel: {
      if (!isRemovePathFromMapPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isMapPanelState(panel)) {
        return state;
      }

      const nextData = panel.data.filter(
        (path) => path.id !== action.payload.pathId,
      );
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForMap(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.RemoveSeriesFromPlotPanel: {
      if (!isRemoveSeriesFromPlotPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isPlotPanelState(panel)) {
        return state;
      }

      const nextData = panel.data.filter(
        (series) => series.id !== action.payload.seriesId,
      );

      // Remove any empty axes from the config.
      let config = panel.config;
      const toRemove = panel.data.find(
        (series) => series.id === action.payload.seriesId,
      );
      if (toRemove) {
        const axis = toRemove.config?.yAxis;
        if (
          axis &&
          config?.extraYAxes &&
          Object.hasOwn(config.extraYAxes, axis)
        ) {
          const willAxisBeEmpty = !!nextData.find(
            (series) => series.config?.yAxis === axis,
          );
          if (willAxisBeEmpty) {
            const { [axis]: _defunct, ...nextExtraAxes } = config.extraYAxes;
            config = {
              ...config,
              extraYAxes: nextExtraAxes,
            };
          }
        }
      }
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          config,
          data: nextData,
          title: titleForPlot(nextData),
        },
      };

      if (nextData.length === 0) {
        // Clean up state potentially left behind in stateful color scale
        cyclingColorScale.resetForPanel(action.payload.panelId);
      }

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.ReplaceState: {
      if (!isReplaceStateAction(action)) {
        return state;
      }
      return action.payload.state;
    }
    case ActionType.ReplacePanelsAndLayout: {
      if (!isReplacePanelsAndLayoutAction(action)) {
        return state;
      }
      return {
        ...state,
        panels: action.payload.panels,
        layout: action.payload.layout,
      };
    }
    case ActionType.ResizeLayouts: {
      if (!isResizeLayoutsAction(action)) {
        return state;
      }

      const { layout1, layout2 } = action.payload;

      const nextState = {
        ...state,
        layout: updateLayout(state.layout, layout1.id, {
          relativeSize: layout1.relativeSize,
        }),
      };

      return {
        ...nextState,
        layout: updateLayout(nextState.layout, layout2.id, {
          relativeSize: layout2.relativeSize,
        }),
      };
    }
    case ActionType.SetAllLayoutsResizing: {
      if (!isSetAllLayoutsResizingAction(action)) {
        return state;
      }
      const { isResizing } = action.payload;
      return setIsLayoutResizing(state, state.layout.children, isResizing);
    }
    case ActionType.SetEventVisibility: {
      if (!isSetEventVisibilityAction(action)) {
        return state;
      }
      const { eventId, isVisible } = action.payload;
      const existingEventConfig = (state.events ?? {})[eventId];

      const updatedEvents = {
        ...state.events,
        [eventId]: {
          ...existingEventConfig,
          isVisible,
        },
      };
      return {
        ...state,
        events: updatedEvents,
      };
    }
    case ActionType.SetEventsVisibility: {
      if (!isSetVisibilityForAllEventsAction(action)) {
        return state;
      }
      const { isVisible, eventIds } = action.payload;
      const updatedEvents = {
        ...state.events,
      };
      eventIds.forEach((eventId) => {
        updatedEvents[eventId] = {
          ...updatedEvents[eventId],
          isVisible,
        };
      });
      return {
        ...state,
        events: updatedEvents,
      };
    }
    case ActionType.SetImagePanelConfig: {
      if (!isSetImagePanelConfigAction(action)) {
        return state;
      }
      const { panelId, config } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isImagePanelState(panel)) {
        return state;
      }

      const updatedPanel = {
        ...panel,
        config: {
          // Lowest priority is default config (to respect it not being optional), then existing config, then new config
          rotation: 0,
          stretchToFitCanvas: true,
          ...panel.config,
          ...config,
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetLogPanelConfig: {
      if (!isSetLogPanelConfigAction(action)) {
        return state;
      }
      const { panelId, config } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isLogPanelState(panel)) {
        return state;
      }

      // Normalize config: don't persist empty search terms or empty column filters
      const normalizedConfig = { ...config };

      // Remove searchFilter if term is empty or whitespace
      if (
        normalizedConfig.searchFilter !== undefined &&
        normalizedConfig.searchFilter.term.trim() === ""
      ) {
        normalizedConfig.searchFilter = undefined;
      }

      // Remove columnFilters if empty object
      if (
        normalizedConfig.columnFilters !== undefined &&
        Object.keys(normalizedConfig.columnFilters).length === 0
      ) {
        normalizedConfig.columnFilters = undefined;
      }

      const updatedPanel = {
        ...panel,
        config: {
          ...panel.config,
          ...normalizedConfig,
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetLayoutResizing: {
      if (!isSetLayoutResizingAction(action)) {
        return state;
      }
      const { layoutIds, isResizing } = action.payload;

      const layouts = layoutIds.map((id) => {
        const layout = selectLayoutById(state, id);
        if (layout === undefined) {
          throw new Error(`Invalid layoutId: ${id}`);
        }
        return layout;
      });

      return setIsLayoutResizing(state, layouts, isResizing);
    }
    case ActionType.SetDataSource: {
      if (!isSetDataSourceAction(action)) {
        return state;
      }
      const { topicData, panelId, segmentId } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || (isPlotPanelState(panel) && segmentId === undefined)) {
        return state;
      }
      let updatedPanel = panel;
      if (isPlotPanelState(panel)) {
        if (Array.isArray(topicData)) {
          return state;
        }
        const idx = panel.data.findIndex((series) => series.id === segmentId);
        if (idx === -1) {
          return state;
        }
        const prev = panel.data[idx];
        if (isEqual(prev.data, topicData)) {
          return state;
        }

        const next = {
          ...prev,
          data: topicData,
        };
        const series = [...panel.data];
        series.splice(idx, 1, next);
        updatedPanel = {
          ...panel,
          data: series,
          title: titleForPlot(series),
        };
      }
      if (isMapPanelState(panel)) {
        if (!Array.isArray(topicData)) {
          return state;
        }
        const idx = panel.data.findIndex((path) => path.id === segmentId);
        if (idx === -1) {
          return state;
        }
        const prev = panel.data[idx];
        if (isEqual(prev.data, topicData)) {
          return state;
        }

        const next = {
          ...prev,
          id: idForMap(topicData),
          data: topicData,
        };
        const paths = [...panel.data];
        paths.splice(idx, 1, next);
        updatedPanel = {
          ...panel,
          data: paths,
          title: titleForMap(paths),
        };
      }
      if (isLogPanelState(panel)) {
        if (Array.isArray(topicData)) {
          return state;
        }
        const idx = panel.data.findIndex(
          (data) => data.messagePath.id === segmentId,
        );
        if (idx === -1) {
          return state;
        }
        const prev = panel.data[idx];
        if (isEqual(prev, topicData)) {
          return state;
        }

        const nextData = [...panel.data];
        nextData.splice(idx, 1, topicData);
        updatedPanel = {
          ...panel,
          data: nextData,
          title: titleForLogPanel(nextData),
        };
      }
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetPlotPanelConfig: {
      if (!isSetPlotPanelConfigAction(action)) {
        return state;
      }
      const { panelId, config } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isPlotPanelState(panel)) {
        return state;
      }
      const updateAxis = (
        original: PlotAxisConfig | undefined,
        updated: Partial<PlotAxisConfig> | undefined,
      ): PlotAxisConfig | undefined => {
        if (original === undefined && updated === undefined) {
          return undefined;
        }
        return {
          ...original,
          ...updated,
        };
      };

      // Only add y axes that are actually used to the panel config.
      const usedYAxes = new Set<PlotYAxis>(["y"]);
      for (const d of panel.data) {
        if (d.config?.yAxis) {
          usedYAxes.add(d.config.yAxis);
        }
      }

      const extraYAxes: Record<string, PlotAxisConfig> = {};
      for (const k of Object.keys(panel.config?.extraYAxes ?? {})) {
        const axis = k as PlotYAxis;
        const original = panel.config?.extraYAxes?.[axis];
        if (usedYAxes.has(axis) && original) {
          extraYAxes[axis] = original;
        }
      }
      if (config.extraYAxes) {
        for (const k of Object.keys(config.extraYAxes)) {
          const axis = k as PlotYAxis;
          const updated = updateAxis(
            panel.config?.extraYAxes?.[axis],
            config.extraYAxes?.[axis],
          );
          if (updated && usedYAxes.has(axis)) {
            extraYAxes[axis] = updated;
          }
        }
      }
      const updatedConfig: PlotPanelConfig = {
        // Lowest priority is default config (to respect it not being optional), then existing config, then new config
        timeBoundsSource: DEFAULT_PLOT_PANEL_CONFIG.timeBoundsSource,
        showEvents: true,
        ...panel.config,
        ...config,
        yAxis: updateAxis(panel.config?.yAxis, config?.yAxis),
        extraYAxes: extraYAxes,
      };

      const updatedPanel: PlotPanelState = {
        ...panel,
        config: updatedConfig,
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetSeriesConfig: {
      if (!isSetSeriesConfigAction(action)) {
        return state;
      }

      const { panelId, seriesId, config } = action.payload;
      const panel = (state.panels ?? {})[panelId];

      if (!panel || !isPlotPanelState(panel)) {
        return state;
      }
      const updatedPanel: PlotPanelState = {
        ...panel,
        data: panel.data.map((series): PlotSeries => {
          if (series.id === seriesId) {
            const updatedConfig: PlotSeriesConfig = {
              lineColor: assignSeriesColor(panel.id, series.data),
              ...series.config,
              ...config,
            };
            return {
              ...series,
              config: updatedConfig,
            };
          }
          return series;
        }),
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetSeriesVisibility: {
      if (!isSetSeriesVisibilityAction(action)) {
        return state;
      }
      const { panelId, seriesId, visible } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isPlotPanelState(panel)) {
        return state;
      }
      const updatedPanel = {
        ...panel,
        data: panel.data.map((series) => {
          if (series.id === seriesId) {
            return {
              ...series,
              visible,
            };
          }
          return series;
        }),
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetPanelTitle: {
      if (!isSetPanelTitleAction(action)) {
        return state;
      }
      const { panelId, title } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel) {
        return state;
      }

      const updatedPanels = {
        ...state.panels,
        [panelId]: {
          ...panel,
          title,
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetPathStyle: {
      if (!isSetPathStyleAction(action)) {
        return state;
      }

      const { panelId, pathId, style } = action.payload;
      const panel = (state.panels ?? {})[panelId];

      if (!panel || !isMapPanelState(panel)) {
        return state;
      }
      const updatedPanel = {
        ...panel,
        data: panel.data.map((path) => {
          if (path.id === pathId) {
            return {
              ...path,
              style: {
                ...path.style,
                ...style,
              },
            };
          }
          return path;
        }),
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetPathVisibility: {
      if (!isSetPathVisibilityAction(action)) {
        return state;
      }
      const { panelId, pathId, visible } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isMapPanelState(panel)) {
        return state;
      }
      const updatedPanel = {
        ...panel,
        data: panel.data.map((path) => {
          if (path.id === pathId) {
            return {
              ...path,
              visible,
            };
          }
          return path;
        }),
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetPathCoordinateUnits: {
      if (!isSetPathCoordinateUnitsAction(action)) {
        return state;
      }
      const { panelId, pathId, coordinateUnits } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isMapPanelState(panel)) {
        return state;
      }
      const updatedPanel = {
        ...panel,
        data: panel.data.map((path) => {
          if (path.id === pathId) {
            return {
              ...path,
              coordinateUnits,
            };
          }
          return path;
        }),
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetThreeDPanelConfig: {
      if (!isSetThreeDPanelConfigAction(action)) {
        return state;
      }
      const { panelId, config } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isThreeDPanelState(panel)) {
        return state;
      }

      const updatedPanel: ThreeDPanelState = {
        ...panel,
        config: {
          ...panel.config,
          ...config,
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.AddThreeDEntity: {
      if (!isAddThreeDEntityAction(action)) {
        return state;
      }
      const { panelId, entity } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isThreeDPanelState(panel)) {
        return state;
      }

      const updatedPanel: ThreeDPanelState = {
        ...panel,
        data: {
          entities: {
            ...panel.data.entities,
            [entity.id]: entity,
          },
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.UpdateThreeDEntity: {
      if (!isUpdateThreeDEntityAction(action)) {
        return state;
      }
      const { panelId, entityId, updates } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isThreeDPanelState(panel)) {
        return state;
      }

      const updatedPanel: ThreeDPanelState = {
        ...panel,
        data: {
          entities: {
            ...panel.data.entities,
            [entityId]: { ...panel.data.entities[entityId], ...updates },
          },
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetDocumentPanelFile: {
      if (!isSetDocumentPanelFileAction(action)) {
        return state;
      }
      const { panelId, data } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isDocumentPanelState(panel)) {
        return state;
      }
      const updatedPanel = {
        ...panel,
        data,
        title: data.fileName,
      };
      return {
        ...state,
        panels: {
          ...state.panels,
          [panel.id]: updatedPanel,
        },
      };
    }
    case ActionType.CreateDocumentPanelWithFile: {
      if (!isCreateDocumentPanelWithFileAction(action)) {
        return state;
      }
      const id = crypto.randomUUID();
      const panel: DocumentPanelState = {
        id,
        type: PanelType.Document,
        data: action.payload.data,
        title: action.payload.data.fileName,
      };
      const nextState: State = {
        ...state,
        panels: {
          ...state.panels,
          [id]: panel,
        },
      };
      const placement = action.payload.placement ?? {
        siblingLayout: state.layout,
        orientation: LayoutOrientation.LEFT,
      };
      const layoutItem: LayoutItem = {
        id,
        isResizing: false,
        relativeSize: 1,
        type: LayoutType.Panel,
      };
      return addItemToLayout(nextState, layoutItem, placement);
    }
    case ActionType.RemoveThreeDEntity: {
      if (!isRemoveThreeDEntityAction(action)) {
        return state;
      }
      const { panelId, entityId } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isThreeDPanelState(panel)) {
        return state;
      }

      const updatedEntities = { ...panel.data.entities };
      delete updatedEntities[entityId];

      const updatedPanel: ThreeDPanelState = {
        ...panel,
        data: {
          entities: updatedEntities,
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    default:
      return state;
  }
}
